//#region IMPORT
import React, { useEffect, useMemo, useRef, useState } from 'react';
import QrCode from 'qrcode.react';
import { useDispatch, useSelector } from 'react-redux';
import Commons from '../../../../../Services/Common/Common';
import { showSnackbar } from '../../../../../Reducer/SnackbarReducer';
import { TbmActions } from '../../../../../Reducer/TbmReducer';
import { useDelete1, useDelete2 } from './QRDialog_Query';
import { useNavigate } from 'react-router-dom';

//#region 헬퍼
/** 본인(관리자) 행인지 확인 */
function IsSelfParticipant(att, myUserId) {
  if (att?.userId == null || myUserId == null) return false;
  return att.userId === myUserId;
}

/** 본인(관리자) 행을 항상 맨 위로 */
function SortParticipantsSelfFirst(participants, myUserId) {
  const list = [...(participants ?? [])];
  list.sort((a, b) => {
    const aSelf = IsSelfParticipant(a, myUserId);
    const bSelf = IsSelfParticipant(b, myUserId);
    if (aSelf && !bSelf) return -1;
    if (!aSelf && bSelf) return 1;
    return 0;
  });
  return list;
}

/** 체크·퇴장 대상이 되는 userId (본인 제외) */
function GetSelectableUserIds(sortedParticipants, myUserId) {
  return sortedParticipants
    .filter((att) => !IsSelfParticipant(att, myUserId) && att?.userId != null)
    .map((att) => att.userId);
}

/** participants 갱신 시 선택 유지해도 되는 id 집합 */
function BuildValidSelectableIdSet(participants, myUserId) {
  return new Set(
    (participants ?? [])
      .filter((att) => att?.userId != null && att.userId !== myUserId)
      .map((att) => att.userId)
  );
}
//#endregion

export default function QRDialog({ onStart, ActiveButton }) {
  //#region 변수
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const qrModal = useSelector((state) => state.tbm.qrModal);
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const { statusBar, navBar } = useSelector((state) => state.systemBar);
  const { sessionData, participants } = useSelector((state) => state.tbm);

  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 0
  );

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const mainRef = useRef(null);
  const [activeTab, setActiveTab] = useState('qr');
  const [selectedParticipantIds, setSelectedParticipantIds] = useState(
    () => new Set()
  );
  const [tbmEndConfirmOpen, setTbmEndConfirmOpen] = useState(false);

  const myUserId = Commons.DecryptoAES(userInfo.userid);
  //#endregion

  //#region 파생값
  // 시스템 바(statusBar·navBar) 제외 남은 세로 공간의 90% (웹/PWA는 보통 inset 0)
  const dialogHeightPx = useMemo(() => {
    const remaining = Math.max(0, viewportHeight - statusBar - navBar);
    return remaining * 0.9;
  }, [viewportHeight, statusBar, navBar]);

  // 참가자 목록(participant 테이블 데이터)
  const participantList = useMemo(
    () => SortParticipantsSelfFirst(participants, myUserId),
    [participants, myUserId]
  );

  // 선택 가능한 userId 목록
  const selectableUserIds = useMemo(
    () => GetSelectableUserIds(participantList, myUserId),
    [participantList, myUserId]
  );

  // 전체선택 체크 여부
  const isAllSelected =
    selectableUserIds.length > 0 &&
    selectableUserIds.every((id) => selectedParticipantIds.has(id));
  //#endregion

  //#region 이펙트
  // 다이얼로그 열릴 때 탭·선택 초기화
  useEffect(() => {
    if (!qrModal) return;
    setActiveTab('qr');
    setSelectedParticipantIds(new Set());
    setTbmEndConfirmOpen(false);
  }, [qrModal]);

  // 서버 목록 변동 시 잘못된 선택 제거
  useEffect(() => {
    const valid = BuildValidSelectableIdSet(participants, myUserId);
    setSelectedParticipantIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [participants, myUserId]);
  //#endregion

  //#region 쿼리
  const { runAsync: Delete1Async } = useDelete1();
  const { runAsync: Delete2Async } = useDelete2();
  //#endregion

  //#region 핸들러
  const HandleRequestClose = () => {
    if (sessionData?.sessionStatus === 'READY') {
      dispatch(
        showSnackbar({
          message: 'TBM 시작 준비가 완료되었습니다. 먼저 TBM을 시작해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    dispatch(TbmActions.setQrModal(false));
  };

  const ScrollMainToTop = () => {
    mainRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  // 탭 변경
  const HandleSwitchTab = (tabId) => {
    setActiveTab(tabId);
    ScrollMainToTop();
  };

  // 전체 선택 체크
  const HandleToggleSelectAll = () => {
    if (selectableUserIds.length === 0) return;
    const everyOn = selectableUserIds.every((id) =>
      selectedParticipantIds.has(id)
    );
    if (everyOn) {
      setSelectedParticipantIds(new Set());
      return;
    }
    setSelectedParticipantIds(new Set(selectableUserIds));
  };

  // 선택 체크
  const HandleToggleSelectOne = (userId) => {
    if (userId == null || userId === '') return;
    const uid = userId;
    const row = participantList.find((a) => a.userId === uid);
    if (!row || IsSelfParticipant(row, myUserId)) return;

    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  // 선택된 인원 퇴장
  const HandleKickSelected = async () => {
    const targetUserIds = [...selectedParticipantIds];
    const kickedCount = targetUserIds.length;
    if (kickedCount === 0) return;

    const sessionCd = sessionData?.sessionCd;
    if (!sessionCd) {
      dispatch(
        showSnackbar({
          message: '세션 정보가 없습니다.',
          type: 'error',
        })
      );
      return;
    }

    try {
      await Delete2Async({ sessionCd, targetUserIds });
      ActiveButton?.({
        type: 'kickParticipant',
        targetUserIds,
      });
      dispatch(
        showSnackbar({
          message: `${kickedCount}명을 퇴장시켰습니다.`,
          type: 'success',
        })
      );
      setSelectedParticipantIds(new Set());
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message ?? '참가자 삭제에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };

  // TBM 시작
  const HandleTbmStart = () => {
    onStart?.({ sessionCd: sessionData.sessionCd });
  };

  // TBM 종료 확인
  const HandleTbmEndConfirm = async () => {
    const sessionCd = sessionData?.sessionCd;
    if (!sessionCd) {
      dispatch(
        showSnackbar({
          message: '세션 정보가 없습니다.',
          type: 'error',
        })
      );
      setTbmEndConfirmOpen(false);
      return;
    }

    try {
      await Delete1Async({ sessionCd });
      setTbmEndConfirmOpen(false);
      ActiveButton?.({ type: 'tbmEnd' });
      dispatch(
        showSnackbar({
          message: 'TBM을 종료했습니다.',
          type: 'success',
        })
      );
      ActiveButton({
        type: 'end',
        content: true,
      });
      navigate('/Home');
      dispatch(TbmActions.setQrModal(false));
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message ?? 'TBM 종료에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };
  //#endregion

  if (!qrModal) {
    return null;
  }

  const overlayStyle = {
    paddingTop: statusBar,
    paddingBottom: navBar,
    paddingLeft: 16,
    paddingRight: 16,
  };

  const dialogStyle = {
    height: dialogHeightPx,
    maxHeight: dialogHeightPx,
  };

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center overflow-y-auto bg-black/30"
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) HandleRequestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="TBM 관리"
        style={dialogStyle}
        className="relative flex w-full flex-col overflow-hidden rounded-3xl bg-white shadow-2xl sm:max-w-md"
      >
        <header className="sticky top-0 z-50 flex h-16 w-full items-center justify-between bg-[#ffffff] px-6">
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full transition-all duration-200 hover:bg-[#dde5d4] active:scale-95"
            onClick={HandleRequestClose}
          >
            <span className="material-symbols-outlined text-[#1e6d00]">
              close
            </span>
          </button>
          <h1 className="text-lg font-bold tracking-tight text-[#1e6d00]">
            TBM 관리
          </h1>
          <div className="h-10 w-10" aria-hidden="true" />
        </header>

        <nav className="sticky top-16 z-40 flex w-full border-b border-[#becbb3]/30 bg-[#ffffff] px-4">
          <button
            type="button"
            className={[
              'flex-1 py-4 text-sm font-bold transition-colors',
              activeTab === 'qr'
                ? 'border-b-2 border-[#1e6d00] text-[#1e6d00]'
                : 'text-[#3f4a38]',
            ].join(' ')}
            onClick={() => HandleSwitchTab('qr')}
          >
            QR코드
          </button>
          <button
            type="button"
            className={[
              'flex-1 py-4 text-sm font-bold transition-colors',
              activeTab === 'paricipant'
                ? 'border-b-2 border-[#1e6d00] text-[#1e6d00]'
                : 'text-[#3f4a38]',
            ].join(' ')}
            onClick={() => HandleSwitchTab('paricipant')}
          >
            참가자
          </button>
        </nav>

        <main ref={mainRef} className="w-full flex-1 overflow-y-auto px-4">
          {activeTab === 'qr' && (
            <div className="flex min-h-full flex-col items-center justify-center py-4">
              <section className="relative">
                <div className="rounded-2xl border border-[#becbb3]/10 bg-white p-6 text-center shadow-sm">
                  <div className="mb-4">
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#b1f096] px-3 py-1 text-xs font-semibold text-[#386f25]">
                      <span className="material-symbols-outlined text-xs">
                        qr_code_scanner
                      </span>
                      실시간 출석 체크
                    </span>
                  </div>

                  <div className="mx-auto flex h-48 w-48 items-center justify-center rounded-xl bg-[#eff6e5] p-4">
                    <div className="flex h-full w-full items-center justify-center mix-blend-multiply">
                      <QrCode
                        value={
                          sessionData.sessionCd
                            ? sessionData.sessionCd.toString()
                            : ''
                        }
                        size={160}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-xl font-bold tracking-tight text-[#171d13]">
                      현장 입장을 위해 스캔하세요
                    </p>
                    <p className="mt-1 text-sm text-[#3f4a38]">
                      QR 코드를 스캔하면 자동으로 명단에 등록됩니다.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'paricipant' && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between px-2">
                <h2 className="text-lg font-black text-[#171d13]">
                  참가자 목록 ({participantList.length}명)
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#3f4a38]">
                    전체 선택
                  </span>
                  <input
                    className="h-5 w-5 rounded-full border-[#becbb3] text-[#1e6d00] focus:ring-[#1e6d00]"
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={HandleToggleSelectAll}
                    disabled={selectableUserIds.length === 0}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {participantList.length === 0 ? (
                  <div className="rounded-2xl border border-[#becbb3]/20 bg-white p-6 text-center text-sm text-[#3f4a38]">
                    참가자가 없습니다.
                  </div>
                ) : (
                  participantList.map((att, idx) => {
                    const isSelf = IsSelfParticipant(att, myUserId);
                    const rowUid = att.userId != null ? att.userId : null;

                    return (
                      <label
                        key={att.userId ?? `${att.name}-${idx}`}
                        className={[
                          'flex items-center rounded-2xl border border-[#becbb3]/20 p-4 shadow-sm transition-colors',
                          isSelf
                            ? 'cursor-default bg-[#f0f4eb]' // 호스트(본인) 배경색 차별화
                            : 'cursor-pointer bg-white active:bg-[#eff6e5]',
                        ].join(' ')}
                      >
                        {/* 체크박스 영역: 호스트일 경우 뱃지로 대체 */}
                        <div className="mr-4 flex h-6 w-6 shrink-0 items-center justify-center">
                          {isSelf ? (
                            <span className="material-symbols-outlined text-[20px] text-[#1e6d00]">
                              lock
                            </span>
                          ) : (
                            <input
                              className="h-5 w-5 rounded-full border-[#becbb3] text-[#1e6d00] focus:ring-[#1e6d00]"
                              type="checkbox"
                              checked={
                                rowUid != null &&
                                selectedParticipantIds.has(rowUid)
                              }
                              onChange={() => HandleToggleSelectOne(att.userId)}
                            />
                          )}
                        </div>

                        <div className="flex flex-1 items-center gap-3">
                          {/* 프로필 이미지 */}
                          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#becbb3]/10 bg-[#e9f1df]">
                            {att.img ? (
                              <img
                                alt="Participant"
                                className="h-full w-full object-cover"
                                src={att.img}
                              />
                            ) : (
                              <span className="material-symbols-outlined text-[28px] text-[#3f4a38]">
                                person
                              </span>
                            )}
                          </div>

                          {/* 정보 영역 */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-[15px] font-bold text-[#171d13]">
                                {att.name}
                              </h3>
                              {isSelf && (
                                <span className="rounded-md bg-[#1e6d00] px-1.5 py-0.5 text-[10px] font-bold text-white">
                                  TBM 진행자
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-[#3f4a38]">
                              <span className="material-symbols-outlined !text-[12px]">
                                schedule
                              </span>
                              체크인: {att.entranceDt}
                            </p>
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </main>

        {activeTab === 'qr' && (
          <div className="w-full p-4">
            <section className="grid grid-cols-2 gap-3">
              <button
                type="button"
                className={[
                  'rounded-full px-4 py-3 text-sm font-bold text-white shadow-md transition-all active:scale-95',
                  'bg-[#1e6d00]',
                  sessionData?.sessionStatus === 'READY'
                    ? 'opacity-100'
                    : 'cursor-not-allowed opacity-50',
                ].join(' ')}
                onClick={HandleTbmStart}
                disabled={sessionData?.sessionStatus === 'OPEN'}
              >
                TBM 시작
              </button>
              <button
                type="button"
                className={[
                  'rounded-full border border-[#becbb3]/30 px-4 py-3 text-sm font-bold text-[#3f4a38] transition-all active:scale-95',
                  'bg-[#e9f1df] opacity-100',
                ].join(' ')}
                onClick={() => setTbmEndConfirmOpen(true)}
              >
                TBM 종료
              </button>
            </section>
          </div>
        )}
        {activeTab === 'paricipant' && (
          <div className="w-full p-4">
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#ba1a1a] py-4 text-base font-black text-white shadow-lg transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={HandleKickSelected}
              disabled={selectedParticipantIds.size === 0}
            >
              <span className="material-symbols-outlined">logout</span>
              선택된 인원 퇴장
            </button>
          </div>
        )}

        {tbmEndConfirmOpen && (
          <div
            className="absolute inset-0 z-[100] flex items-center justify-center rounded-3xl bg-black/45 p-6"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) setTbmEndConfirmOpen(false);
            }}
          >
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="tbm-end-confirm-title"
              className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                id="tbm-end-confirm-title"
                className="text-center text-base font-bold text-[#171d13]"
              >
                정말 TBM을 종료하시겠습니까?
              </h2>
              <p className="mt-2 text-center text-sm text-[#3f4a38]">
                종료 후 세션 정보가 초기화됩니다.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="rounded-full border border-[#becbb3] py-3 text-sm font-bold text-[#3f4a38] transition-all active:scale-95"
                  onClick={() => setTbmEndConfirmOpen(false)}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="rounded-full bg-[#1e6d00] py-3 text-sm font-bold text-white shadow-md transition-all active:scale-95"
                  onClick={HandleTbmEndConfirm}
                >
                  종료
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
